/**
 * @type {import('./index.d').sortByKey}
 */
export function sortByKey(data, key, order = 'asc') {
  if (!Array.isArray(data) && typeof data !== 'object') {
    console.error('Invalid input. Expecting an array or an object.');
    return;
  }

  if (Array.isArray(data)) {
    return sortArrayByKey(data, key, order);
  } else {
    return sortObjectByKey(data, key, order);
  }
}

/**
 * @type {import('./index.d').sortArrayByKey}
 */
export function sortArrayByKey(array, key, order = 'asc') {
  const sortOrder = order === 'desc' ? -1 : 1;

  return array.sort((a, b) => {
    const aRaw = a[key];
    const bRaw = b[key];
    // Convert null values to NaN to ensure they are treated as non-numeric.
    // This allows numeric values to be prioritized in the sort order, while
    // non-numeric values (including NaN) are deprioritized.
    const aNum = aRaw == null || isNaN(Number(aRaw)) ? null : Number(aRaw);
    const bNum = bRaw == null || isNaN(Number(bRaw)) ? null : Number(bRaw);
    const aIsNum = aNum !== null;
    const bIsNum = bNum !== null;
    // Numeric comparison if both are numbers, otherwise fall back to lexicographical comparison.
    if (aIsNum && bIsNum) {
      return (aNum - bNum) * sortOrder;
    }
    // Prioritize numeric values over non-numeric ones for consistent ordering.
    if (aIsNum) return -1 * sortOrder;
    if (bIsNum) return 1 * sortOrder;
    // Both are non-numeric: fallback to locale-aware lexicographical comparison.
    return String(aRaw).localeCompare(String(bRaw), undefined, { numeric: true, sensitivity: 'base' }) * sortOrder;
  });
}

/**
 * @type {import('./index.d').sortObjectByKey}
 */
export function sortObjectByKey(object, key, order) {
  const entries = Object.entries(object);

  const sortedEntries = sortArrayByKey(entries, key, order);

  const result = {};
  for (const [key, value] of sortedEntries) {
    result[key] = value;
  }

  return result;
}

/**
 * @type {import('./index.d').isEqual}
 */
export function isEqual(value1, value2) {
  if (typeof value1 !== typeof value2) {
    return false;
  }

  if (typeof value1 === 'object' && value1 !== null && value2 !== null) {
    const keys1 = Object.keys(value1);
    const keys2 = Object.keys(value2);

    if (keys1.length !== keys2.length) {
      return false;
    }

    for (const key of keys1) {
      if (!isEqual(value1[key], value2[key])) {
        return false;
      }
    }

    return true;
  }

  return value1 === value2;
}

/**
 * @type {import('./index.d').deepClone}
 */
export function deepClone(obj) {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }

  if (Array.isArray(obj)) {
    const newArray = [];
    for (let i = 0; i < obj.length; i++) {
      newArray[i] = deepClone(obj[i]);
    }
    return newArray;
  }

  const newObj = {};
  for (const key in obj) {
    if (Object.prototype.hasOwnProperty.call(obj, key)) {
      newObj[key] = deepClone(obj[key]);
    }
  }
  return newObj;
}
